/**
 * Dev-only preview mode: render any page as a signed-in user, without logging in.
 *
 * Why this is safe to have in the tree
 * -----------------------------------
 * Every export below is gated on `process.env.NODE_ENV === 'development'`.
 * Create React App inlines that literal at build time, so in a production
 * build the constant folds to `false` and the minifier removes the fake user
 * and every branch that depends on it. There is no runtime flag, no header and
 * no query parameter that can switch this on in production -- the code is not
 * in the bundle at all. src/__tests__/no-preview-in-production-build.test.js
 * asserts exactly that against the built output (it runs under `npm test`), so
 * the guarantee is checked rather than trusted.
 *
 * Usage (dev server only):
 *   npm start
 *   http://localhost:3000/?preview=1
 *   http://localhost:3000/arrow-vision?preview=1
 *   http://localhost:3000/admin?preview=1
 *
 * It only fakes the *frontend* auth state so pages render. It mints no real
 * token, so any request that actually reaches the backend is still rejected by
 * verifyFirebaseToken. Network calls are stubbed below to keep pages from
 * erroring while you look at them.
 */

const IS_DEV = process.env.NODE_ENV === 'development';

export const isPreviewMode = () => {
  if (!IS_DEV) return false;
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).has('preview');
};

// Shaped like the bits of a Firebase User the app actually reads.
export const previewUser = !IS_DEV
  ? null
  : {
      uid: 'preview-user',
      displayName: 'Preview User',
      email: 'preview@example.invalid',
      photoURL: null,
      // Deliberately not a real token: if this ever escaped to the backend it
      // would be rejected, which is the behaviour we want.
      getIdToken: async () => 'preview-mode-not-a-real-token',
    };

// Sample data so pages render populated rather than empty or mid-error.
export const previewCredits = 7;

export const previewImages = !IS_DEV
  ? []
  : [
      {
        id: 'preview-1',
        prompt: 'Looking north-east from the bridge',
        featureType: 'arrow-vision',
        timestamp: { _seconds: 1758700000, _nanoseconds: 0 },
        imageData: null,
      },
      {
        id: 'preview-2',
        prompt: 'Beach without the crowd',
        featureType: 'person-remover',
        timestamp: { _seconds: 1758600000, _nanoseconds: 0 },
        imageData: null,
      },
    ];
